import {
  BadgeCheck,
  CircleDollarSign,
  Eye,
  LockKeyhole,
  Route,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  WandSparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { aiTools, freeTools } from '../data/tools';
import { Seo } from '../lib/seo';
import { absoluteUrl } from '../lib/site';
import profilePhoto from '../assets/anupama-herath.webp';

const principles = [
  {
    icon: LockKeyhole,
    title: 'Use AI only when the job needs AI',
    description: 'Compression, resizing, cropping, conversion, colors and metadata-clean exports stay separate as free browser utilities.'
  },
  {
    icon: ScanSearch,
    title: 'Match the workflow to the defect',
    description: 'Blur, noise, low resolution, photo damage, object removal and generative expansion are different image problems and should be treated that way.'
  },
  {
    icon: CircleDollarSign,
    title: 'Show the cost before processing',
    description: 'Credit estimates are tied to the selected AI workflow and image workload instead of hiding processing behind an “unlimited” label.'
  },
  {
    icon: Eye,
    title: 'Review the result, not just the effect',
    description: 'Faces, text, products, edges and repeating patterns deserve a before-and-after check whenever AI can reconstruct or generate detail.'
  }
];

const useCases = [
  ['Photographers & creators', 'Clean low-light noise, repair blur, improve portraits, relight a scene, resize social images and prepare web-ready exports without running every file through the same AI filter.'],
  ['Designers & agencies', 'Vectorize raster artwork, remove backgrounds or objects, extend a canvas for campaign layouts, sample colors, build palettes and prepare client assets in several formats.'],
  ['Online stores', 'Enhance product detail, create cleaner backgrounds, remove distractions, prepare consistent marketplace dimensions and compress final listing images for faster pages.'],
  ['Families & archives', 'Scan and restore old photographs, recover damaged faces carefully, colorize monochrome copies as an interpretation and preserve the untouched original alongside the restored version.'],
  ['Social & content teams', 'Prepare Instagram, TikTok, YouTube, LinkedIn and X assets with the correct composition, then use AI only when the source genuinely needs repair or generated space.'],
  ['Everyday image tasks', 'Resize a picture for a form, convert PNG or JPG to a modern format, crop a profile image, remove metadata before sharing or find a color value from a screenshot without spending credits.']
] as const;

export function AboutPage() {
  const aboutSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Nexhance AI',
      url: absoluteUrl('/'),
      description: 'An online image platform with focused AI enhancement and editing workflows plus separate free browser image utilities.',
      logo: absoluteUrl('/favicon-256x256.png'),
      founder: { '@type': 'Person', name: 'Anupama Herath' },
      publishingPrinciples: absoluteUrl('/methodology'),
      privacyPolicy: absoluteUrl('/privacy'),
      knowsAbout: [
        'AI image enhancement', 'AI image upscaling', 'old photo restoration', 'AI object removal',
        'AI background changer', 'generative expand', 'image vectorization', 'image compression',
        'image resizing', 'WebP conversion', 'social media image resizing'
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Anupama Herath',
      jobTitle: 'Founder of Nexhance AI',
      image: absoluteUrl('/anupama-herath-preview.webp'),
      worksFor: { '@type': 'Organization', name: 'Nexhance AI', url: absoluteUrl('/') }
    }
  ];

  const groupedAi = [
    ['Enhance & repair', aiTools.filter((tool) => ['enhance', 'upscale', 'restore', 'face-restore', 'deblur', 'denoise', 'sharpen'].includes(tool.slug))],
    ['AI editing', aiTools.filter((tool) => ['relight', 'object-remover', 'text-remover', 'image-extender', 'background-changer', 'photo-colorizer', 'image-editor'].includes(tool.slug))],
    ['Design & commerce', aiTools.filter((tool) => ['remove-background', 'vectorize', 'product-photo'].includes(tool.slug))]
  ] as const;

  return (
    <section className="page-section about-page">
      <Seo
        title="About Nexhance AI - AI Image Tools & Free Image Utilities"
        description="Learn how Nexhance AI combines image enhancement, upscaling, restoration, object removal, generative editing and free browser utilities in one practical image workflow platform."
        path="/about"
        jsonLd={aboutSchema}
        image="/og-nexhance-ai.png"
        imageAlt="Nexhance AI image editing and enhancement platform"
      />
      <div className="shell">
        <div className="page-hero about-me-hero">
          <p className="eyebrow">About Nexhance AI</p>
          <h1>A practical image platform for fixing, editing and preparing images without treating every task as an AI problem.</h1>
          <p>Nexhance AI brings together focused AI image enhancement and generative editing workflows with a separate set of free browser utilities. The idea is simple: use model-based processing when pixels need reconstruction, and use a normal image utility when the job is only compression, resizing, cropping, format conversion, social dimensions, color work or metadata cleanup.</p>
        </div>

        <div className="about-manifesto">
          <div>
            <span className="tool-icon"><Sparkles size={22} /></span>
            <p className="eyebrow">What the site is built around</p>
            <h2>One image problem, one clear workflow.</h2>
          </div>
          <div>
            <p>A blurry photograph, a noisy low-light image, a tiny logo, a faded family scan and a product photo with a distracting background are not the same problem. Nexhance AI separates those jobs so a user can choose the correction that matches the visible defect instead of stacking several unrelated filters.</p>
            <p>The same logic applies to everyday file operations. If an image only needs a smaller file size, new dimensions, a social crop or a WebP-style export, there is no reason to spend AI credits. Those tasks stay in a clearly labeled Free Image Utilities section.</p>
          </div>
        </div>

        <div className="principles-grid">
          {principles.map((principle) => {
            const Icon = principle.icon;
            return <article key={principle.title}><Icon /><h2>{principle.title}</h2><p>{principle.description}</p></article>;
          })}
        </div>

        <section className="about-tools-overview">
          <div className="section-heading centered">
            <p className="eyebrow">25 image tools</p>
            <h2>AI enhancement and editing are grouped separately from free image utilities.</h2>
            <p>The tool library covers common image-quality searches such as AI image enhancer, image upscaler, old photo restoration, object remover, background changer, generative expand, image compressor, image resizer, WebP converter and social media image resizer.</p>
          </div>
          <div className="about-tool-groups">
            {groupedAi.map(([label, group]) => (
              <article key={label}>
                <p className="eyebrow">AI workflow</p>
                <h3>{label}</h3>
                <div className="about-tool-links">
                  {group.map((tool) => <Link key={tool.slug} to={`/tools/${tool.slug}`}><strong>{tool.name}</strong><span>{tool.description}</span></Link>)}
                </div>
              </article>
            ))}
            <article>
              <p className="eyebrow">Free · 0 credits</p>
              <h3>Browser image utilities</h3>
              <div className="about-tool-links">
                {freeTools.map((tool) => <Link key={tool.slug} to={`/tools/${tool.slug}`}><strong>{tool.name}</strong><span>{tool.description}</span></Link>)}
              </div>
            </article>
          </div>
        </section>

        <section className="about-audience-section">
          <div className="section-heading centered">
            <p className="eyebrow">Real use cases</p>
            <h2>Built for the way images move from source file to final delivery.</h2>
          </div>
          <div className="about-audience-grid">
            {useCases.map(([title, description]) => <article key={title}><h3>{title}</h3><p>{description}</p></article>)}
          </div>
        </section>

        <section className="about-routing-section">
          <div className="about-routing-copy">
            <p className="eyebrow">How processing is separated</p>
            <h2>Free utilities handle deterministic edits. AI workflows handle reconstruction and generation.</h2>
            <p>For free browser tools, the work is performed locally in a supported browser and uses 0 credits. That includes compression, normal resizing, cropping, format conversion, social media resizing, color picking, palette extraction and metadata-clean image exports.</p>
            <p>AI workflows are different because a model may infer missing detail, reconstruct a removed area or generate new surroundings. Those jobs use account credits, and the current estimate appears before processing. Failed AI jobs are handled through the existing credit-restoration workflow when a usable result is not returned.</p>
          </div>
          <div className="about-routing-cards">
            <article><Route /><span>Step 1</span><strong>Diagnose</strong><p>Choose blur, noise, low resolution, damage, background, object cleanup, generated space or a normal file operation.</p></article>
            <article><ShieldCheck /><span>Step 2</span><strong>Use the lightest tool</strong><p>Prefer a free browser utility when no reconstruction or semantic image editing is needed.</p></article>
            <article><BadgeCheck /><span>Step 3</span><strong>Review and export</strong><p>Check changed details against the source, then resize, convert or compress the final delivery copy separately.</p></article>
          </div>
        </section>

        <section className="about-profile-card">
          <div className="about-profile-photo-wrap">
            <img src={profilePhoto} alt="Portrait of Anupama Herath" className="about-profile-photo" />
          </div>
          <div className="about-profile-copy">
            <p className="eyebrow">Who runs Nexhance AI</p>
            <h2>Anupama Herath</h2>
            <p className="about-profile-role">Founder of Nexhance AI · Designer focused on practical visual tools</p>
            <p>My background is in creative and digital design work, so I tend to judge an image by whether it is actually usable in a real project, not by how dramatic the before-and-after effect looks. I built Nexhance AI around that mindset: make the workflow understandable, keep normal utilities free, and be clear when AI can reconstruct details rather than recover exact facts.</p>
            <p>The platform is independently operated and developed as a long-term product. That means the tool library, pricing explanations, support pages, privacy information and image guides are maintained together rather than treating the written content as decoration around an API.</p>
            <div className="about-profile-highlights">
              <span><WandSparkles size={16} /> Focused image workflows</span>
              <span><ShieldCheck size={16} /> Clear processing distinction</span>
              <span><BadgeCheck size={16} /> Practical guides and support</span>
            </div>
            <div className="about-plan-links">
              <Link to="/tools" className="button button-primary">Explore all tools</Link>
              <Link to="/contact" className="button button-ghost">Contact support</Link>
            </div>
          </div>
        </section>

        <section className="about-operator-section about-personal-section">
          <div>
            <p className="eyebrow">Trust & quality</p>
            <h2>Useful content should explain the trade-offs, not repeat a search keyword.</h2>
          </div>
          <div>
            <p>Nexhance AI publishes guides around real questions: when to use 2× or 4× upscaling, how to avoid plastic-looking denoise, when a crop is better than generative expand, how to review object-removal artifacts, what image format fits a website, and what local browser processing means for privacy.</p>
            <p>Popular search language is used where it accurately describes the feature, but the goal is not to create dozens of pages that differ only by wording. The guide library is organized around tools and workflows so readers can move from a problem, to a decision, to the correct tool and final export.</p>
            <div className="about-plan-links">
              <Link to="/guides" className="button button-primary">Browse image guides</Link>
              <Link to="/methodology" className="button button-ghost">Editorial methodology</Link>
            </div>
          </div>
        </section>

        <section className="about-editorial-section">
          <div>
            <p className="eyebrow">What we do not promise</p>
            <h2>AI can improve an image without turning guesses into facts.</h2>
          </div>
          <div>
            <p>Image enhancement, face restoration, object removal, background generation and photo colorization can create convincing visual information that was not explicitly captured in the source. For historical, identity-sensitive, legal or product-critical work, the original remains the reference and the output should be reviewed as an edited result.</p>
            <ul className="about-editorial-list">
              <li><strong>Originals matter:</strong> keep the source before AI processing, compression, conversion or metadata removal.</li>
              <li><strong>Faces and text need review:</strong> plausible detail can still be wrong.</li>
              <li><strong>Product accuracy matters:</strong> check labels, colors, shape, condition and included features before publishing.</li>
              <li><strong>Colorization is interpretive:</strong> a monochrome photo usually cannot prove the original color of every object.</li>
              <li><strong>Free utilities are not AI:</strong> normal image operations stay separate so users do not spend credits unnecessarily.</li>
            </ul>
          </div>
        </section>
      </div>
    </section>
  );
}
